import { useEffect, useState } from 'react';
import { CalendarSheet, CalendarRangeSheet, Calendar, type DateRange } from '@/components/ui/calendar';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/core/utils/cn';
import { useClientTranslation } from '@/lib/core/i18n';
import { formatShortDate } from '@/lib/core/utils';
import { getMewsHourSlots, type MewsHourSlot } from '@/services/platform/data/booking/mews';
import type { ExperienceWhenSheetProps, HourTab } from '../types';
import { ExperienceHourSlots } from './hourSlots';

/** Milliseconds of an hour, minus a minute — see `handleHourConfirm`. */
const SLOT_LENGTH_MS = 60 * 60 * 1000 - 60 * 1000;

export function ExperienceWhenSheet({
  isOpen,
  onClose,
  cmsProps,
  isRange = false,
  isHourly = false,
  selectedFrom,
  selectedTo,
  onSelect,
  serviceId,
  enterpriseId,
  adults,
  childrenCount,
  ageConfig,
  locale,
}: ExperienceWhenSheetProps) {
  const t = useClientTranslation();

  const [range, setRange] = useState<DateRange | undefined>(
    selectedFrom ? { from: selectedFrom, to: selectedTo ?? undefined } : undefined
  );
  const [date, setDate] = useState<Date | undefined>(selectedFrom ?? undefined);

  /* Hourly */

  const [tab, setTab] = useState<HourTab>('date');
  const [slots, setSlots] = useState<MewsHourSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotEpochMs, setSlotEpochMs] = useState<number | null>(null);

  const loadSlots = async (day: Date) => {
    if (!serviceId || !enterpriseId) return;
    setSlotsLoading(true);
    try {
      setSlots(
        await getMewsHourSlots({
          serviceId,
          enterpriseId,
          day,
          adults: adults ?? 0,
          children: childrenCount ?? 0,
          ageConfig,
          locale,
        })
      );
    } finally {
      setSlotsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setRange(selectedFrom ? { from: selectedFrom, to: selectedTo ?? undefined } : undefined);
    setDate(selectedFrom ?? undefined);
    setTab('date');
    setSlots([]);
    setSlotEpochMs(selectedFrom?.getTime() ?? null);

    // Reopening on a day that was already chosen has to re-ask for that day's hours: the grid and
    // the tab label both read off `slots`, so without this the hour the user picked last time shows
    // as nothing at all.
    if (isHourly && selectedFrom) void loadSlots(selectedFrom);
  }, [isOpen]);

  const assistance =
    cmsProps?.assistanceTitle && cmsProps?.assistanceDescription
      ? {
          title: cmsProps.assistanceTitle,
          description: cmsProps.assistanceDescription,
          descriptionForMember: cmsProps.assistanceDescriptionForMember,
        }
      : undefined;

  // ── Hourly: date + hour in one sheet ────────────────────────────────────────
  //
  // Modelled on the SevenRooms `WhenSheet`, which solves the same problem with the same two tabs.
  // The slot query lives here rather than on the flow's `useMewsCategories`: confirming a slot
  // narrows `booking.dateFrom/dateTo` to that single hour, which re-runs that hook and would leave
  // it holding one time unit instead of the day's worth this grid needs.
  if (isHourly) {
    const handleDaySelect = (day: Date | undefined) => {
      setDate(day);
      setSlotEpochMs(null);
      setSlots([]);
      if (!day) return;
      setTab('time');
      void loadSlots(day);
    };

    const handleHourConfirm = () => {
      if (slotEpochMs === null) return;
      // The end lands inside the same hourly time unit, which is what makes Mews resolve the
      // reservation to exactly one unit. Mews normalises the bounds itself, so this offset only
      // has to stay within the hour — it is not the reservation's real end.
      onSelect({ from: new Date(slotEpochMs), to: new Date(slotEpochMs + SLOT_LENGTH_MS) });
      onClose();
    };

    const dateTabLabel = date ? formatShortDate(date) : t('App.Labels.Date');
    const hourTabLabel = slots.find(s => s.startEpochMs === slotEpochMs)?.label ?? t('App.Labels.Time');

    return (
      <Drawer
        open={isOpen}
        onOpenChange={open => {
          if (!open) onClose();
        }}
      >
        <DrawerContent className="border-jncquoi-neutral-200 h-[80dvh] min-h-[min(650px,100dvh)] border-t-[0.5px]">
          <DrawerHeader className="shrink-0 gap-2 pt-8 pb-3">
            <div className="flex items-center gap-8">
              <DrawerTitle className="flex-1 text-[32px] font-medium tracking-[0.5px]">
                {cmsProps?.title}
              </DrawerTitle>
              <DrawerClose
                className="flex size-11 cursor-pointer items-center justify-center rounded-full"
                aria-label="Close"
              >
                <Icon
                  name="xmark"
                  width={24}
                  height={24}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000"
                />
              </DrawerClose>
            </div>
            {cmsProps?.subtitle && (
              <DrawerDescription className="text-[16px] text-[#707070]">
                {cmsProps.subtitle}
              </DrawerDescription>
            )}
          </DrawerHeader>

          <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto">
            <div className="flex flex-col pb-6">
              {/* Tabs */}
              <div className="mx-auto mt-3 mb-5 flex w-fit items-center rounded-[20px] bg-[#f4f4f4] p-1">
                {(['date', 'time'] as HourTab[]).map(tabKey => (
                  <button
                    key={tabKey}
                    type="button"
                    onClick={() => setTab(tabKey)}
                    disabled={tabKey === 'time' && !date}
                    className={cn(
                      'font-pp-neue-montreal rounded-[20px] px-4 py-1.5 text-[14px] tracking-[0.5px] transition-all duration-200 disabled:opacity-40',
                      tab === tabKey
                        ? 'bg-white font-normal text-[#1a1a1a]'
                        : 'bg-[#f4f4f4] font-semibold text-[#1a1a1a]'
                    )}
                  >
                    {tabKey === 'date' ? dateTabLabel : hourTabLabel}
                  </button>
                ))}
              </div>

              {tab === 'date' && (
                <Calendar
                  isOpen={isOpen}
                  selected={date}
                  onSelect={handleDaySelect}
                  locale={locale}
                  assistance={assistance}
                />
              )}

              {tab === 'time' && (
                <ExperienceHourSlots
                  slots={slots}
                  selectedEpochMs={slotEpochMs}
                  isLoading={slotsLoading}
                  onSelect={slot => setSlotEpochMs(slot.startEpochMs)}
                />
              )}
            </div>

            <div className="sticky bottom-0 w-full shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
              <Button
                variant="primary-solid"
                palette="dark"
                size="lg"
                className="w-full"
                disabled={!date || slotEpochMs === null}
                onClick={handleHourConfirm}
              >
                {cmsProps?.selectButtonLabel ?? t('App.Labels.Select')}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  if (isRange) {
    const handleConfirm = () => {
      if (!range?.from) return;
      onSelect({ from: range.from, to: range.to ?? range.from });
      onClose();
    };

    return (
      <CalendarRangeSheet
        isOpen={isOpen}
        onClose={onClose}
        title={cmsProps?.title}
        subtitle={cmsProps?.subtitle}
        selected={range}
        onSelect={setRange}
        selectLabel={cmsProps?.selectButtonLabel}
        onConfirm={handleConfirm}
        assistance={assistance}
      />
    );
  }

  const handleConfirm = () => {
    if (!date) return;
    onSelect({ from: date, to: date });
    onClose();
  };

  return (
    <CalendarSheet
      isOpen={isOpen}
      onClose={onClose}
      title={cmsProps?.title}
      subtitle={cmsProps?.subtitle}
      selected={date}
      onSelect={setDate}
      selectLabel={cmsProps?.selectButtonLabel}
      onConfirm={handleConfirm}
      assistance={assistance}
    />
  );
}
